/**
 * Utility function to update user store with subscription status
 *
 * This function can be called manually to update the user's subscription status
 * without triggering the automatic effect from useSubscriptionStatusCheck hook.
 * Useful when subscription changes occur and we need to update the UI immediately.
 */

import {
	getCurrentSubscription,
	getSubscriptionPlans,
} from "@/services/subscription-service";
import { useUserStore } from "@/stores/user-store";
import type { IUser } from "@/stores/user-store";

/**
 * Updates the user store with current subscription status
 * @param user - Current user object from the store
 * @returns Promise<void>
 */
export const updateSubscriptionStatus = async (
	user: IUser | null,
): Promise<void> => {
	if (!user) return;

	const { setUser } = useUserStore.getState();

	// Skip check for super admins (they get free unlimited access)
	if (user?.role?.access_level && user.role.access_level >= 1000) {
		setUser({
			...user,
			hasActiveSubscription: true,
			hasPlusSubscription: true, // Super admins get Plus features
			subscriptionStatus: "active" as any,
		});
		return;
	}

	// Handle trial users (they have their own expiration system)
	if (user?.isTrial || user?.is_trial) {
		// Check if trial is still valid (expiration date is in the future)
		const trialExpirationDate =
			user?.trialExpirationDate || user?.trial_expiration_date;

		if (trialExpirationDate) {
			const expirationDate = new Date(trialExpirationDate);
			const now = new Date();
			const isTrialValid = expirationDate > now;

			if (isTrialValid) {
				// Trial is still valid - set subscription status to active
				setUser({
					...user,
					hasActiveSubscription: true,
					hasPlusSubscription: false, // Trial users don't get Plus features
					subscriptionStatus: "active" as any,
				});
				return;
			} else {
				// Trial has expired - don't set active subscription
				setUser({
					...user,
					hasActiveSubscription: false,
					hasPlusSubscription: false,
					subscriptionStatus: null,
				});
				return;
			}
		} else {
			// Trial user but no expiration date - treat as expired
			setUser({
				...user,
				hasActiveSubscription: false,
				hasPlusSubscription: false,
				subscriptionStatus: null,
			});
			return;
		}
	}

	try {
		const [subscription, plans] = await Promise.all([
			getCurrentSubscription(),
			getSubscriptionPlans(),
		]);

		if (!subscription) {
			// No subscription found
			setUser({
				...user,
				hasActiveSubscription: false,
				hasPlusSubscription: false,
				subscriptionStatus: null,
			});
			return;
		}

		const now = new Date();
		const periodEnd = new Date(subscription.currentPeriodEnd);
		const daysUntilExpiration = Math.ceil(
			(periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
		);

		const isLapsed =
			subscription.status === "past_due" ||
			subscription.status === "canceled" ||
			(subscription.status === "active" && daysUntilExpiration < 0);

		// Find the current plan details
		const currentPlan = plans.find(
			(plan) => plan.id.toString() === subscription.planId.toString(),
		);

		// Check if this is a Plus subscription by plan name or ID
		const isPlusSubscription =
			subscription.status === "active" &&
			(currentPlan?.name
				?.toLowerCase()
				.includes("plus") || // Plus plan by name
				Number(subscription.planId) === 1 || // Super Admin plan (gets Plus features)
				Number(subscription.planId) === 3 || // FacilityAI Plus Subscription plan
				Number(subscription.planId) === 17); // Legacy plan ID

		// Update user store with subscription info
		setUser({
			...user,
			hasActiveSubscription: !isLapsed && subscription.status === "active",
			hasPlusSubscription: !isLapsed && isPlusSubscription,
			subscriptionStatus: subscription.status as any,
		});
	} catch (error) {
		console.error("Failed to update subscription status:", error);
		// Don't throw - just log the error
	}
};
